/**
 * Generates a SHA-1 digest of the supplied string, and returns
 * it as a base64 string optionally truncated to the first 'length'
 * characters.
 *
 * @param {string} message to digest.
 * @return {Promise<string>} base64-encoded digest.
 */
export async function shortSafeDigest(message, length = 0) {
  const msgUint8 = new TextEncoder().encode(message)
  if (!crypto.subtle) {
    throw 'Requires secure origin (localhost or https:)'
  }
  const hashBuffer = await crypto.subtle.digest("SHA-1", msgUint8)
  const hashArray = new Uint8Array(hashBuffer)
  const byteString = String.fromCodePoint(...hashArray)
  const base64 = btoa(byteString)
  const base64Safe = base64
    .replaceAll('+','-')
    .replaceAll('/','_')
    .replaceAll('=','')
  return length ? base64Safe.substring(0, length) : base64Safe
}

/**
 * Generates a SHA-1 digest of the supplied string, and returns
 * it as a hex string optionally truncated to the first 'length'
 * characters.
 *
 * @param {string} message to digest.
 * @return {Promise<string>} hex-encoded digest.
 */
export async function shortHexDigest(message, length = 0) {
  const msgUint8 = new TextEncoder().encode(message)
  if (!crypto.subtle) {
    throw 'Requires secure origin (localhost or https:)'
  }
  const hashBuffer = await crypto.subtle.digest("SHA-1", msgUint8)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')

  return length ? hashHex.substring(0, length) : hashHex
}

/**
 * Returns the standard app id for the supplied app URL.
 *
 * This is the shortSafeDigest of the URL truncated to 8 base64 chars,
 * i.e. distributed over a 48-bit space which seems reasonable for the
 * number of apps on a given daemon.
 *
 * @param {string | URL} url of the app whose id is to be computed.s
 * @return {Promise<string>} default id for the app, commonly used in prefixes.
 */
export async function getAppId(url) {
  if (typeof url === 'string') {
    url = new URL(url)
  }
  const appId = await shortSafeDigest(String(url), 8)
  return appId
}
